import React, { useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import HeroBadge from "@/components/HeroBadge";
import useScrollReveal from "@/hooks/useScrollReveal";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, BadgePercent, FileText, ArrowRight, CheckCircle2, Stethoscope, FlaskConical, Pill, HeartPulse, Sparkles, ShieldCheck, Activity } from "lucide-react";

export default function Corporate() {
  useScrollReveal();
  const [form, setForm] = useState({ company_name: "", contact_person: "", mobile: "", email: "", employee_count: 0, notes: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/corporate-leads", form);
      toast.success("Proposal request sent. Our corporate desk will reach out within 24 hours.");
      setForm({ company_name: "", contact_person: "", mobile: "", email: "", employee_count: 0, notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>Corporate Membership · <span className="text-[#A53A2A]">Bulk Pricing</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              Healthcare for your <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">whole team.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">
              Offer the Nirmal Privilege Card to your employees as a corporate wellness benefit. Bulk discounts, easy onboarding, and one consolidated invoice.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      {/* <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[
          [Users, "Easy onboarding", "Single Excel upload — we generate cards for every employee."],
          [Building2, "One invoice", "Consolidated GST-compliant invoice issued to your company."],
          [FileText, "Custom benefits", "Add company branding and custom welcome emails on request."],
        ].map(([Icon, t, d], i) => (
          <div key={t} className="reveal lux-card rounded-2xl p-7" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1E4F87] to-[#0F2540] text-white grid place-items-center shadow-lg"><Icon className="h-6 w-6" /></div>
            <h3 className="mt-5 font-display text-lg font-bold text-[#0F2540]">{t}</h3>
            <p className="mt-2 text-sm text-[#5C6B7A]">{d}</p>
          </div>
        ))}
      </section> */}

      {/* Corporate Healthcare Privilege Program — intro + advantages */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="corp-program-section">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 reveal">
            <span className="overline">For organisations</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540] leading-tight">
              Corporate Healthcare<br /><span className="text-[#A53A2A]">Privilege Program.</span>
            </h2>
            <p className="mt-5 text-[#5C6B7A] text-lg leading-relaxed">
              Organisations can extend NPC healthcare privileges to employees and their families through customised corporate healthcare partnerships — built around your team's wellness goals.
            </p>
            <div className="mt-7 flex items-center gap-3 text-sm text-[#5C6B7A]">
              <ShieldCheck className="h-5 w-5 text-[#1F7A3D]" /> Trusted by 40+ regional corporates · Adajan, Surat
            </div>
          </div>

          <div className="lg:col-span-6 reveal" style={{ animationDelay: "120ms" }}>
            <div className="rounded-3xl bg-white ring-1 ring-[#E1E8F0] p-8 shadow-[0_30px_60px_-30px_rgba(15,37,64,0.18)]">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FCEDE7] text-[10px] uppercase tracking-[0.22em] font-bold text-[#A53A2A]">
                <Sparkles className="h-3.5 w-3.5" /> Corporate advantages
              </div>
              <div className="mt-5 grid gap-4">
                {[
                  [Users,      "Healthcare for employees & dependents",  "Coverage extends across employee families on a single corporate partnership."],
                  [Activity,   "Wellness-focused engagement",            "Preventive screenings, awareness drives and onsite camp options."],
                  [Sparkles,   "Priority access to awareness initiatives", "Early access to workshops, webinars and health calendar events."],
                ].map(([Icon, t, d], i) => (
                  <div key={t} className="flex items-start gap-4 group" data-testid={`corp-advantage-${i}`}>
                    <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#A53A2A] to-[#7A2A1E] grid place-items-center text-white flex-shrink-0 shadow-[0_10px_22px_-10px_rgba(165,58,42,0.6)] group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display text-base font-bold text-[#0F2540]">{t}</div>
                      <p className="text-sm text-[#5C6B7A] mt-1 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Included Benefits — 5 categories */}
      <section className="bg-[#FAFBFC] border-y border-[#E1E8F0]" data-testid="corp-included-benefits-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="max-w-3xl reveal">
            <span className="overline">Programme inclusions</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540] leading-tight">
              Included <span className="text-[#A53A2A]">benefits.</span>
            </h2>
            <p className="mt-4 text-[#5C6B7A] text-lg leading-relaxed">
              A structured set of healthcare privileges across OPD, diagnostics, pharmacy, IPD, and family wellness — designed to support your team end-to-end.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: Stethoscope, title: "OPD Benefits", accent: "from-[#A53A2A] to-[#7A2A1E]",
                items: [
                  "Unlimited consultations with eligible in-house consultants during regular OPD hours*",
                  "Follow-up consultations included",
                  "Selected super-speciality consultations available at defined concessional rates",
                ],
              },
              {
                icon: FlaskConical, title: "Diagnostic Benefits", accent: "from-[#1E4F87] to-[#0F2540]",
                items: [
                  "Special rates on pathology and radiology investigations",
                  "Benefits on preventive health check-up packages",
                ],
              },
              {
                icon: Pill, title: "Pharmacy Benefits", accent: "from-[#C89B3C] to-[#9C7421]",
                items: [
                  "Eligible discounts on in-house pharmacy purchases",
                ],
              },
              {
                icon: HeartPulse, title: "IPD Benefits", accent: "from-[#7A2A1E] to-[#4D1C13]",
                items: [
                  "Benefits on selected hospital billing components",
                ],
              },
              {
                icon: Users, title: "Family & Wellness Benefits", accent: "from-[#1F7A3D] to-[#155F2D]",
                items: [
                  "Preventive healthcare communication and reminders",
                  "Access to healthcare camps and awareness initiatives",
                  "Community health workshops and webinars",
                ],
              },
              {
                icon: Sparkles, title: "Corporate Support", accent: "from-[#0F2540] to-[#1B3253]",
                items: [
                  "Single GST invoice and centralised onboarding",
                  "Custom welcome communication for employees",
                  "Dedicated relationship manager for queries",
                ],
              },
            ].map((b, i) => (
              <div key={b.title} className="reveal group relative rounded-2xl bg-white p-6 ring-1 ring-[#E1E8F0] hover:ring-[#C89B3C]/45 hover:-translate-y-1 hover:shadow-[0_28px_50px_-28px_rgba(15,37,64,0.25)] transition-all duration-500 overflow-hidden" style={{ animationDelay: `${i * 60}ms` }} data-testid={`corp-included-card-${i}`}>
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${b.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${b.accent} grid place-items-center text-white shadow-[0_10px_24px_-10px_rgba(15,37,64,0.4)] group-hover:scale-110 transition-transform duration-500`}>
                  <b.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-[#0F2540]">{b.title}</h3>
                <ul className="mt-3 space-y-2">
                  {b.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#5C6B7A] leading-relaxed">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#5C6B7A] italic">*Subject to plan eligibility, hospital policy and applicable terms.</p>
        </div>
      </section>

      {/* Lead form */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="rounded-[2rem] mesh-dark p-8 lg:p-14 relative overflow-hidden reveal">
          <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="overline text-[#C89B3C]">Request a proposal</span>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-white">Get a custom corporate quote.</h2>
              <p className="mt-4 text-white/80">Tell us about your team and our corporate desk will draft a tailored proposal within one business day.</p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                {["Volume pricing & GST-compliant invoice", "Pre-onboarded employee cards", "Custom welcome communication"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#C89B3C]" /> {x}</li>
                ))}
              </ul>
            </div>
            <form onSubmit={submit} className="rounded-2xl bg-white p-6 lg:p-7 space-y-3 shadow-2xl" data-testid="corp-lead-form">
              <Inp label="Company name" v={form.company_name} s={(v) => setForm({ ...form, company_name: v })} required testid="cl-company" />
              <Inp label="Contact person" v={form.contact_person} s={(v) => setForm({ ...form, contact_person: v })} required testid="cl-person" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Inp label="Mobile" v={form.mobile} s={(v) => setForm({ ...form, mobile: v })} required testid="cl-mobile" />
                <Inp label="Email" type="email" v={form.email} s={(v) => setForm({ ...form, email: v })} required testid="cl-email" />
              </div>
              <Inp label="Employee count" type="number" v={form.employee_count} s={(v) => setForm({ ...form, employee_count: Number(v) || 0 })} testid="cl-count" />
              <div>
                <label className="text-sm font-medium text-[#0F2540]">Notes</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 text-black outline-none outline-none focus:border-[#1E4F87]" data-testid="cl-notes" />
              </div>
              <button disabled={loading} className="btn-primary w-full justify-center" data-testid="corp-submit-btn">
                {loading ? "Sending…" : <>Request proposal <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Inp({ label, v, s, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-[#0F2540]">{label}{required && " *"}</label>
      <input type={type} value={v} required={required} onChange={(e) => s(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 text-black outline-none focus:border-[#1E4F87]"
        data-testid={testid} />
    </div>
  );
}
